"use strict";

import _ from "lodash";
import { getAge as getUserAge } from "utils/date";
import App from "containers/app";
import AccountOverviewPage from "containers/account-overview-page";
import DrinkTrackerPage from "containers/drink-tracker-page";
import DynamicPage from "containers/dynamic-page";
import LoginPage from "containers/login-page";
import ModuleOverviewPage from "containers/module-overview-page";
import MoodTrackerPage from "containers/mood-tracker-page";
import OverviewPage from "containers/overview-page";
import PrivacyPage from "containers/privacy-page";
import TermsPage from "containers/terms-page";
import PasswordResetRequestPage from "containers/password-reset-request-page";
import PaymentInformationPage from "containers/payment-information-page";
import PlanSelectionPage from "containers/plan-selection-page";
import RegistrationPage from "containers/registration-page";
import ResetPasswordPage from "containers/reset-password-page";
import ScreenerPage from "containers/screener-page";
import ScreenerPlanSelectionPage from "containers/screener-plan-page";
import SectionOverviewPage from "containers/section-overview-page";
import ToolOverviewPage from "containers/tool-overview-page";
import UserProfilePage from "containers/user-profile-page";
import MedicalHistoryPage from "containers/medical-history-page";
import UrgeTrackerPage from "containers/urge-tracker-page";
import ProgressPage from "containers/progress-page";
import DrinkTrackerFeedbackPage from "containers/drink-tracker-feedback-page";
import UrgeTrackerFeedbackPage from "containers/urge-tracker-feedback-page";
import AgreementTrackerPage from "containers/agreement-tracker-page";
import AdminDashboard from "containers/admin-crud";

import Actions from "actions";
import DrinkTrackerFormPage from "./containers/DrinkTrackerFormPage.component.web";
import RemindersPage from "./containers/RemindersPage.component.web";
import { getSimplifiedTools, getTool, getTools } from "api/tools";
const logOutUser = Actions.logOutUser;

export default (store) => {
  const routeByUserRole = (nextState, replaceState, callback) => {
    const state = store.getState(),
      { user } = state.auth || {},
      { roles } = user || {},
      { admin, system } = roles || {},
      isAdmin =
        !!admin?.length || (!!system?.length && system.includes("admin"));
    if (!isAdmin && nextState.location.pathname.includes("/admin")) {
      replaceState("/dashboard", {});
    }
    if (callback) callback();
  };

  const routeByUserStatus = (nextState, replaceState, callback) => {
    let newRoute;
    const pathname = nextState.location.pathname;
    const state = store.getState();
    const user = _.get(state, "auth.user"),
      { roles } = user || {},
      { admin, system } = roles || {},
      isAdmin =
        !!admin?.length || (!!system?.length && system.includes("admin"));
    const userStatus = _.get(user, "status");
    const isUnsubscribed = userStatus === "unsubscribed";
    const isNormal = userStatus === "normal";
    const isLoggedIn = isNormal || isUnsubscribed;

    const { session_brokering } = CUAC_SETTINGS?.GROUP ?? {},
      { medical_history, status } = user ?? {};

    if (!isAdmin && pathname.includes("/admin")) {
      newRoute = "/";
    }

    if (!isLoggedIn) {
      const hasScreener = Boolean(
        _.get(CUAC_SETTINGS, "GROUP.screener_tool_id"),
      );
      if (pathname === "/" && hasScreener) {
        newRoute = "/screener" + nextState.location.search || "";
      } else if (!pathname.startsWith("/login")) {
        newRoute =
          `/login${nextState.location.search?.length ? nextState.location.search + "&" : "?"}redirect=` +
          pathname;
      }
    } else if (isUnsubscribed) {
      if (_.isEmpty(_.get(state, "auth.selectedPlan", {}))) {
        newRoute = "/account/plans" + nextState.location.search || "";
      } else {
        newRoute = "/account/payment" + nextState.location.search || "";
      }
    } else {
      if (Boolean(_.get(CUAC_SETTINGS, "GROUP.allow_skip_profile") === true)) {
        newRoute = pathname;
      }
      // If this user was created by the Rails app and has not yet completed their medical history, send them there
      else if (!!session_brokering && !medical_history && status === "normal") {
        if (pathname !== "/account/medical-history")
          newRoute = `/account/medical-history?new=true`;
      } else {
        if (
          !_.get(user, "dt_dob") ||
          !_.get(user, "weight") ||
          !_.get(user, "gender")
        ) {
          if (pathname === "/account/medical-history") {
            newRoute = pathname;
          } else {
            newRoute = "/account/profile";
          }
        } else {
          newRoute = pathname;
        }
      }
    }
    if (newRoute !== pathname) {
      if (
        CUAC_SETTINGS.GROUP.should_reload_on_tool_navigation &&
        newRoute.startsWith("/screener")
      ) {
        window.location.replace(newRoute);
      } else {
        replaceState(newRoute, {});
      }
    }

    if (callback) {
      callback();
    }
  };

  const requireLoginAndSubscription = (nextState, replaceState, callback) => {
    const state = store.getState();
    const status = _.get(state, "auth.user.status");
    const isUnsubscribed = status === "unsubscribed";
    const isLoggedIn = status === "normal" || isUnsubscribed;
    if (!isLoggedIn) {
      replaceState("/login?redirect=" + nextState.location.pathname, {
        nextPathname: nextState.location.pathname,
      });
    } else if (isUnsubscribed) {
      replaceState("/account", {});
    }
    if (callback) {
      callback();
    }
  };

  const logOut = (nextState, replaceState, callback) => {
    const state = store.getState();
    const userStatus = _.get(state, "auth.user.status");
    const isLoggedIn = userStatus === "normal" || userStatus === "unsubscribed";
    if (isLoggedIn) {
      store.dispatch(logOutUser()).then(() => {
        window.location = "/login";
      });
    }
    if (callback) {
      callback();
    }
  };

  const requireValidResponse =
    (level = "tool") =>
    async (nextState, replaceState, callback) => {
      try {
        let { auth, tools, responses } = store.getState();
        const isLoggedIn = !!auth?.user?._id;

        if (!isLoggedIn) {
          if (_.get(nextState.location.query, "override")) {
            let extraQueryArgs = "";
            for (const key in nextState.location.query) {
              if (key !== "override") {
                extraQueryArgs +=
                  "&" + key + "=" + nextState.location.query[key];
              }
            }
            replaceState(
              "/login?redirect=" +
                nextState.location.pathname +
                "?override=true" +
                extraQueryArgs,
              {
                nextPathname: nextState.location.pathname,
              },
            );
          } else {
            replaceState("/", {
              nextPathname: nextState.location.pathname,
            });
          }

          callback?.();
        } else {
          const activeResponseId = _.get(responses, "activeResponseId");
          const activeResponse = _.get(
            responses,
            activeResponseId + ".response",
          );

          let tool = _.get(tools, `${activeResponse?.tool_id}`);

          // Check if user is trying to access a different tool than their active response
          const requestedSlug = nextState.location.pathname.split("/")[1];
          const activeResponseToolSlug = tool?.slug;

          if (!tool || activeResponseToolSlug !== requestedSlug) {
            try {
              tool = await getToolIfNotPresent(requestedSlug, (to) =>
                replaceState(to, { nextPathname: nextState.location.pathname }),
              );

              // ADD RESPONSE SWITCHING LOGIC HERE
              if (tool && tool._id && auth?.user?.token && auth?.user?._id) {
                const user = auth.user;

                // First, try to load existing responses for this user
                await store.dispatch(
                  Actions.getResponses(user.token, user._id),
                );

                // Wait a moment for responses to load, then check if we have a response for this tool
                await new Promise((resolve) => setTimeout(resolve, 500));

                // Check if we now have a response for this tool
                const updatedState = store.getState();
                const existingResponses = updatedState.responses;
                const responseForThisTool = Object.values(
                  existingResponses,
                ).find((response) => response?.response?.tool_id === tool._id);

                if (responseForThisTool) {
                  // Set this as the active response

                  store.dispatch(
                    Actions.setResponse(
                      user.token,
                      user._id,
                      responseForThisTool.response._id,
                    ),
                  );
                } else {
                  const responseResult = await store.dispatch(
                    Actions.addResponse(user.token, user._id, tool),
                  );

                  // Check if response was created successfully
                  if (responseResult.type === "REQUEST_ADD_RESPONSE_SUCCESS") {
                    // Response created successfully
                  } else {
                    console.error("Failed to create response:", responseResult);
                  }
                }
              }
              // END RESPONSE SWITCHING LOGIC
            } catch (e) {
              console.error("requireValidResponse error:", e);
              replaceState("/", {
                nextPathname: "dashboard", //nextState.location.pathname,
              });
            }
            return;
          }

          // Check if user has wrong tool for their age (fix existing wrong responses)
          if (
            requestedSlug === "checkup" &&
            (tool?.slug === "checkup" || tool?.slug === "checkup25")
          ) {
            const user = _.get(auth, "user");
            const userAge = user?.dt_dob ? getUserAge(user.dt_dob) : null;
            const preferredSlug =
              userAge !== null && userAge < 25 ? "checkup25" : "checkup";

            if (tool.slug !== preferredSlug) {
              // Check if we're already in the middle of fixing this to prevent infinite loop
              const fixingToolMismatch = sessionStorage.getItem(
                "fixing-tool-mismatch",
              );
              if (fixingToolMismatch) {
                sessionStorage.removeItem("fixing-tool-mismatch");
                // Continue with current tool to break the loop
              } else {
                // Find the age-appropriate tool
                const availableTools = user?.tool_ids
                  ?.map((id) => tools[id])
                  .filter(Boolean);
                const correctTool = availableTools.find(
                  (t) => t.slug === preferredSlug,
                );

                if (correctTool && user?.token && user?._id) {
                  sessionStorage.setItem("fixing-tool-mismatch", "true");

                  // Check if user already has a response for the correct tool
                  const existingResponses = Object.values(
                    state.responses,
                  ).filter((r) => r.response);
                  const existingCorrectResponse = existingResponses.find(
                    (r) => r.response.tool_id === correctTool._id,
                  );

                  if (existingCorrectResponse) {
                    try {
                      await store.dispatch(
                        Actions.setResponse(
                          user.token,
                          user._id,
                          existingCorrectResponse.response._id,
                        ),
                      );
                      window.location.reload();
                      return;
                    } catch (e) {
                      console.error(
                        "Failed to set existing response as active:",
                        e,
                      );
                    }
                  } else {
                    try {
                      const responseResult = await store.dispatch(
                        Actions.addResponse(user.token, user._id, correctTool),
                      );
                      if (
                        responseResult.type === "REQUEST_ADD_RESPONSE_SUCCESS"
                      ) {
                        window.location.reload();
                        return;
                      }
                    } catch (e) {
                      console.error(
                        "Failed to create age-appropriate response:",
                        e,
                      );
                    }
                  }

                  sessionStorage.removeItem("fixing-tool-mismatch");
                }
              }
            }
          }

          store.dispatch(Actions.requestGetToolsSuccess([tool]));

          // Ensure tool has metadata before proceeding
          if (
            !tool.metadata ||
            !tool.metadata.modules ||
            !tool.metadata.order
          ) {
            try {
              await waitForReduxStoreValue(
                `tools.${tool?._id}.metadata`,
                (metadata) =>
                  !!(metadata && metadata.modules && metadata.order),
                10000,
              ); // Increased timeout to 10 seconds
              // Get the updated tool with metadata
              const updatedState = store.getState();
              tool = _.get(updatedState, `tools.${tool._id}`);
            } catch (e) {
              console.error("Tool metadata not loaded in time:", e);

              // Try to fetch the tool metadata from backend as a fallback
              try {
                const { auth } = store.getState();
                const userToken = auth?.user?.token;

                if (!userToken) {
                  throw new Error("User token not available");
                }

                const fetchedTool = await getTool(userToken, tool._id);
                if (
                  fetchedTool &&
                  fetchedTool.metadata &&
                  fetchedTool.metadata.modules &&
                  fetchedTool.metadata.order
                ) {
                  tool = fetchedTool;
                  store.dispatch(Actions.requestGetToolSuccess(tool));
                } else {
                  throw new Error("Tool metadata not available from backend");
                }
              } catch (backendError) {
                console.error(
                  "Failed to fetch tool metadata from backend:",
                  backendError,
                );

                // Show user-friendly error and redirect
                store.dispatch(
                  Actions.notify({
                    title: "Tool Not Available",
                    message: `The ${tool?.title?.defaultMessage || tool?.slug || "tool"} is currently not available. Please try again later.`,
                    level: "error",
                    autoDismiss: 5,
                  }),
                );

                // Redirect to dashboard
                replaceState("/", {
                  nextPathname: nextState.location.pathname,
                  fromExternalApp: true,
                });
                return;
              }
            }
          }

          // Verify that we have the essential metadata
          if (
            !tool.metadata ||
            !tool.metadata.modules ||
            !tool.metadata.order
          ) {
            console.error("Tool metadata is incomplete:", tool.metadata);
            store.dispatch(
              Actions.notify({
                title: "Tool Not Available",
                message: `The ${tool?.title?.defaultMessage || tool?.slug || "tool"} configuration is incomplete. Please try again later.`,
                level: "error",
                autoDismiss: 5,
              }),
            );

            // Redirect to dashboard
            replaceState("/", {
              nextPathname: nextState.location.pathname,
              fromExternalApp: true,
            });
            return;
          }

          await checkPathValidity(
            nextState,
            replaceState,
            callback,
            level,
            tool,
          );

          if (callback && typeof callback === "function") {
            callback();
          }
        }
      } catch (e) {
        console.error("requireValidResponse error:", e);
        replaceState("/", {
          nextPathname: "dashboard", //nextState.location.pathname,
        });
      }
    };

  const requireTool = () => {
    return function (nextState, replaceState, callback) {
      const state = store.getState();
      const userStatus = _.get(state, "auth.user.status");
      const isLoggedIn = userStatus === "normal";
      if (!isLoggedIn) {
        if (_.get(nextState.location.query, "override")) {
          replaceState(
            "/login?redirect=" + nextState.location.pathname + "?override=true",
            {
              nextPathname: nextState.location.pathname,
            },
          );
        } else {
          replaceState("/", {
            nextPathname: nextState.location.pathname,
          });
        }
      } else {
        let activeTool = false;
        const tools = _.get(state, "tools", []);
        const path = nextState.location.pathname;
        const toolSlug = path.split("/")[2];
        for (var key in tools) {
          if (tools[key].slug == toolSlug) {
            activeTool = true;
          }
        }
        if (!activeTool) {
          replaceState({ nextPathname: nextState.location.pathname }, "/");
        }
      }

      if (callback) {
        callback();
      }
    };
  };

  const checkPathValidity = async (
    nextState,
    replaceState,
    callback,
    level,
    activeTool,
  ) => {
    const state = store.getState();
    const path = nextState.location.pathname;
    const currentToolSlug = path.split("/")[1];

    // Add comprehensive logging for abstain tool debugging

    console.log("Level:", level);
    console.log("Path:", path);
    console.log("Current tool slug:", currentToolSlug);
    console.log("Active tool:", activeTool);
    console.log("Active tool slug:", activeTool?.slug);

    let pathIsGood = true;
    let reroutePath = "/";

    const getModuleFromPath = (modules, path) => {
      const moduleSlug = path.split("/")[2];
      let delivery = null;
      _.forOwn(modules, (module, moduleId) => {
        if (module.slug === moduleSlug) {
          delivery = module;
        }
      });
      return delivery;
    };

    const getSectionFromPath = (module, path) => {
      const sectionSlug = path.split("/")[3];
      let delivery = null;
      _.forOwn(module.sections, (section, sectionId) => {
        if (section.slug === sectionSlug) {
          delivery = section;
        }
      });
      return delivery;
    };

    const getPageFromPath = (section, path) => {
      const pageSlug = path.split("/")[4];
      let delivery = null;
      _.forOwn(section.pages, (page, pageId) => {
        if (page.slug === pageSlug) {
          delivery = page;
        }
      });
      return delivery;
    };

    let module, section, page;

    try {
      if (activeTool.slug !== currentToolSlug) {
        if (_.get(nextState.location.query, "override")) {
          pathIsGood = true;
          // make this the active tool
          const tools = _.get(state, "tools");
          const responses = _.get(state, "responses");
          const user = _.get(state.auth, "user");
          // loop through tools to find the current slug
          for (var id in tools) {
            if (tools[id].slug === currentToolSlug) {
              // loop through the responses, if we find one, set it as active
              let addResponse = true;
              for (var responseId in responses) {
                if (_.get(responses[responseId].response, "tool_id") === id) {
                  store.dispatch(
                    Actions.setResponse(user.token, user._id, responseId),
                  );
                  addResponse = false;
                }
              }
              // if no response was found, set add it
              if (addResponse) {
                await store.dispatch(
                  Actions.addResponse(user.token, user._id, tools[id]),
                );
              }
            }
          }
        } else {
          pathIsGood = false;
          reroutePath = "/" + activeTool.slug;
        }
      } else {
        if (level === "module" || level === "section" || level === "page") {
          module = getModuleFromPath(activeTool?.metadata?.modules, path);
          if (!module) {
            pathIsGood = false;
            reroutePath = "/" + activeTool.slug;
          }
        }
        if (level === "section" || level === "page") {
          section = module ? getSectionFromPath(module, path) : null;
          if (module && !section) {
            pathIsGood = false;
            reroutePath = "/" + activeTool.slug + "/" + module.slug;
          }
        }
        if (level === "page") {
          page = section ? getPageFromPath(section, path) : null;
          if (module && section && !page) {
            pathIsGood = false;
            reroutePath =
              "/" + activeTool.slug + "/" + module.slug + "/" + section.slug;
          }
        }
      }

      if (!pathIsGood) {
        replaceState(reroutePath, { nextPathname: reroutePath });
      }

      if (callback) {
        callback();
      }
    } catch (e) {
      replaceState("/", { nextPathname: "/" });
    }
  };

  const requireScreener = (nextState, replaceState, callback) => {
    const state = store.getState();
    const userStatus = _.get(state, "auth.user.status", "unregistered");
    const isUnregistered = userStatus === "unregistered";
    const hasScreener = Boolean(_.get(CUAC_SETTINGS, "GROUP.screener_tool_id"));

    if (!isUnregistered) {
      replaceState("/", {});
    }

    if (!hasScreener) {
      replaceState("/register", {
        nextPathname: nextState.location.pathname,
      });
    }
    if (callback) {
      callback();
    }
  };

  const requireScreenerCompletion = (nextState, replaceState, callback) => {
    const state = store.getState();
    const userStatus = _.get(state, "auth.user.status", "unregistered");
    const isUnregistered = userStatus === "unregistered";
    let isOverridden = false;
    if (nextState.location.query.override == "true") {
      isOverridden = true;
    } else if (Boolean(_.get(CUAC_SETTINGS, "GROUP.bypass_screener"))) {
      isOverridden = true;
    }
    if (!isUnregistered) {
      replaceState("/", {});
    }
    const tools = state.tools;
    const responses = _.get(state, "responses");
    const activeResponse = _.get(responses, responses.activeResponseId);
    let userResponses;

    let questionLength = 0;
    let userResponseLength = 0;

    for (var key in tools) {
      if (_.get(tools[key], "metadata.pages")) {
        const screenerPages = tools[key].metadata.pages;
        for (var key in screenerPages) {
          const pageComponents = screenerPages[key].components;
          for (var key in pageComponents) {
            if (_.get(pageComponents[key], "fields")) {
              const fields = _.get(pageComponents[key], "fields");
              questionLength += fields.length;
            }
          }
        }
      }
    }

    userResponses = _.get(activeResponse, "response.responses");
    if (userResponses) {
      userResponseLength = Object.keys(userResponses).length;
    }

    if (userResponseLength) {
      userResponseLength -= [
        "screener_question_score",
        "screener_readiness_score",
      ].filter((special) => special in userResponses).length;
    }

    if (questionLength > userResponseLength && !isOverridden) {
      store.dispatch(
        Actions.notify({
          title: "Please complete the screener",
          message:
            "Please complete the following questions before registering.",
          level: "error",
          autoDismiss: 5,
        }),
      );
      replaceState("/screener/questions?completion_notice=true", {
        nextPathname: nextState.location.pathname,
      });
    }
    if (callback) {
      callback();
    }
  };

  const linkExternalUser = async (nextState, replaceState, callback) => {
    const state = store.getState(),
      { user } = state.auth || {},
      { external_user_id } = nextState.location.query || {};
    if (
      external_user_id &&
      external_user_id.length &&
      (!user.external_user_id || !user.external_user_id.length)
    ) {
      try {
        await store.dispatch(
          Actions.updateUser(user.token, user._id, { external_user_id }),
        );
      } catch (e) {}
    }
    callback();
  };

  const waitForReduxStoreValue = async (
    key,
    isValuePresent = (v) => !!v,
    timeout = 5 * 1000,
  ) => {
    const currentValue = _.get(store.getState(), key);

    if (!!isValuePresent(currentValue)) return Promise.resolve(currentValue);

    return new Promise((res, rej) => {
      // Add a timeout to reject the promise if it takes too long
      const timeoutId = setTimeout(() => {
        rej("TOOL_FETCH_TIMEOUT");
      }, timeout);

      const unsubscribe = store.subscribe(() => {
        const updatedValue = _.get(store.getState(), key);
        if (isValuePresent(updatedValue)) {
          clearTimeout(timeoutId);
          unsubscribe();
          res(updatedValue);
        }
      });
    });
  };

  const getToolIfNotPresent = async (
    slug,
    replaceState = () => {},
    waitForResponses = false,
    mandatoryResponse = false,
  ) => {
    store.dispatch(Actions.updateDisplay("appSpinner", "LOADING_TOOL"));
    const { auth } = store.getState();

    const userToken = auth.user.token;

    let tool;

    try {
      const tools = await waitForReduxStoreValue(
        "tools",
        (tools) => {
          // For checkup, also look for checkup25 or other checkup variants
          if (slug === "checkup") {
            return !!Object.values(tools).find(
              (t) => t.slug === "checkup" || t.slug === "checkup25",
            );
          }
          return !!Object.values(tools).find((t) => slug === t.slug);
        },
        3 * 1000,
      );

      if (slug === "screener") {
        tool = tools[CUAC_SETTINGS.GROUP.screener_tool_id] ?? {
          _id: CUAC_SETTINGS.GROUP.screener_tool_id,
        };
      } else if (slug === "checkup") {
        // For checkup, find the appropriate variant based on user's age
        let { auth } = store.getState();
        let user = auth.user;

        // If user doesn't have DOB or weight in state, fetch fresh user data from database
        if (!user?.dt_dob || typeof user?.weight !== "number") {
          try {
            const freshUserResult = await store.dispatch(
              Actions.getUser(user.token, user._id),
            );
            if (freshUserResult.type === "REQUEST_GET_USER_SUCCESS") {
              user = freshUserResult.payload.user;
            } else {
              console.error(
                "getToolIfNotPresent - failed to fetch fresh user data",
              );
            }
          } catch (e) {
            console.error(
              "getToolIfNotPresent - error fetching fresh user data:",
              e,
            );
          }
        }

        const userAge = user?.dt_dob ? getUserAge(user.dt_dob) : null;

        // Select appropriate checkup tool based on age (default to regular checkup if age unknown)
        const preferredSlug =
          userAge !== null && userAge < 25 ? "checkup25" : "checkup";

        // First try to find the age-appropriate tool
        tool = Object.values(tools).find((t) => t.slug === preferredSlug);

        // If not found, fallback to any checkup variant the user has access to
        if (!tool) {
          tool = Object.values(tools).find(
            (t) => t.slug === "checkup" || t.slug === "checkup25",
          );
        }
      } else {
        tool = Object.values(tools).find((t) => slug === t.slug);
      }
    } catch (e) {
      // Error waiting for redux store - tool not found in store, will fetch from backend
    }

    if (
      !!tool &&
      !!tool.metadata &&
      !!tool.metadata.modules &&
      !!tool.metadata.order
    ) {
      console.log(
        "=== Tool and complete metadata found in store, dispatching success ===",
      );
      store.dispatch(Actions.requestGetToolSuccess(tool));

      if (waitForResponses) {
        await waitForReduxStoreValue(`responses`, (_responses) =>
          mandatoryResponse ? !!_responses[tool._id] : !_responses.isFetching,
        );
      }

      store.dispatch(Actions.updateDisplay("appSpinner", "STOP"));
      return Promise.resolve(tool);
    }

    try {
      // Start fetching tool from BE
      if (
        !!tool &&
        (!tool.metadata || !tool.metadata.modules || !tool.metadata.order)
      ) {
        tool = await getTool(userToken, tool?._id);
      } else if (!tool) {
        tool = (await getTools(userToken))?.find((t) => t.slug === slug);

        if (!tool) {
          throw new Error("TOOL_NOT_FOUND");
        }
      }

      // Verify that we have complete metadata
      if (!tool.metadata || !tool.metadata.modules || !tool.metadata.order) {
        // For screener tools, allow incomplete metadata to proceed
        if (slug === "screener") {
          // Don't throw error for screener - it might work with incomplete metadata
        } else {
          throw new Error("TOOL_METADATA_INCOMPLETE");
        }
      }

      store.dispatch(Actions.requestGetToolSuccess(tool));

      // If necessary wait for tool responses
      if (waitForResponses) {
        await waitForReduxStoreValue(`responses`, (_responses) =>
          mandatoryResponse ? !!_responses[tool._id] : !_responses.isFetching,
        );
      }

      store.dispatch(Actions.updateDisplay("appSpinner", "STOP"));
      return Promise.resolve(tool);
    } catch (e) {
      store.dispatch(Actions.updateDisplay("appSpinner", "STOP"));
      throw e;
    }
  };

  return [
    {
      path: "/",
      component: App,
      onEnter: (nextState, replaceState, callback) => {
        linkExternalUser(nextState, replaceState, callback);
      },
      indexRoute: {
        component: OverviewPage,
        onEnter: (nextState, replaceState, callback) => {
          routeByUserStatus(nextState, replaceState, callback);
        },
      },
      childRoutes: [
        {
          path: "/plan-select",
          component: ScreenerPlanSelectionPage,
        },
        {
          path: "reminders",
          component: RemindersPage,
          onEnter: requireLoginAndSubscription,
        },
        {
          path: "dashboard",
          component: OverviewPage,
          onEnter: (nextState, replaceState, callback) => {
            requireLoginAndSubscription(nextState, replaceState, callback);
          },
        },
        {
          path: "drink-feedback",
          onEnter: (nextState, replaceState, callback) => {
            try {
              requireLoginAndSubscription(nextState, replaceState, () => {
                replaceState("/trackers/drink", {
                  nextPathname: nextState.location.pathname,
                });
                typeof callback === "function" && callback();
              });
            } catch (e) {
              console.error("[ROUTES] Error in drink-feedback redirect:", e);
              replaceState("/trackers/drink", {
                nextPathname: nextState.location.pathname,
              });
              typeof callback === "function" && callback();
            }
          },
        },
        {
          path: "urge-feedback",
          component: UrgeTrackerFeedbackPage,
          onEnter: requireLoginAndSubscription,
        },
        {
          path: "login",
          component: LoginPage,
        },
        {
          path: "logout",
          component: LoginPage,
          onEnter: logOut,
        },
        {
          path: "register",
          component: RegistrationPage,
          onEnter: requireScreenerCompletion,
        },
        {
          path: "forgot-password",
          component: PasswordResetRequestPage,
        },
        {
          path: "reset-password",
          component: ResetPasswordPage,
        },
        {
          path: "reset-password/:reset_key",
          component: ResetPasswordPage,
        },
        {
          path: "privacy",
          component: PrivacyPage,
        },
        {
          path: "terms",
          component: TermsPage,
        },
        {
          path: "admin",
          component: AdminDashboard,
          onEnter: routeByUserRole,
        },
        {
          path: "account",
          component: AccountOverviewPage,
          onEnter: routeByUserStatus,
        },
        {
          path: "account/payment",
          component: PaymentInformationPage,
          onEnter: routeByUserStatus,
        },
        {
          path: "account/profile",
          component: UserProfilePage,
          onEnter: routeByUserStatus,
        },
        {
          path: "account/medical-history",
          component: MedicalHistoryPage,
          onEnter: routeByUserStatus,
        },
        {
          path: "account/plans",
          component: PlanSelectionPage,
          onEnter: routeByUserStatus,
        },
        {
          path: "drinks",
          onEnter: (nextState, replaceState, callback) => {
            try {
              requireLoginAndSubscription(nextState, replaceState, () => {
                replaceState("/trackers/drink", {
                  nextPathname: nextState.location.pathname,
                });
                typeof callback === "function" && callback();
              });
            } catch (e) {
              console.error("[ROUTES] Error in /drinks redirect:", e);
              replaceState("/trackers/drink", {
                nextPathname: nextState.location.pathname,
              });
              typeof callback === "function" && callback();
            }
          },
        },
        {
          path: "drink-tracker(/:id)",
          onEnter: (nextState, replaceState, callback) => {
            try {
              requireLoginAndSubscription(nextState, replaceState, () => {
                replaceState("/trackers/drink", {
                  nextPathname: nextState.location.pathname,
                });
                typeof callback === "function" && callback();
              });
            } catch (e) {
              console.error("[ROUTES] Error in /drink-tracker redirect:", e);
              replaceState("/trackers/drink", {
                nextPathname: nextState.location.pathname,
              });
              typeof callback === "function" && callback();
            }
          },
        },
        {
          path: "urges",
          component: UrgeTrackerPage,
          onEnter: requireLoginAndSubscription,
        },
        {
          path: "moods",
          component: MoodTrackerPage,
          onEnter: requireLoginAndSubscription,
        },
        {
          path: "agreements",
          component: AgreementTrackerPage,
          onEnter: requireLoginAndSubscription,
        },
        {
          path: "screener",
          component: ScreenerPage,
          onEnter: async (next, replace, callback) => {
            try {
              await getToolIfNotPresent("screener", replace);
              return requireScreener(next, replace);
            } catch (error) {
              // If it's a metadata error, try to continue anyway for screener
              if (error.message === "TOOL_METADATA_INCOMPLETE") {
                return requireScreener(next, replace);
              }

              // For other errors, redirect to home
              replace("/", { nextPathname: next.location.pathname });
            } finally {
              typeof callback === "function" && callback?.();
            }
          },
        },
        {
          path: "screener/plan",
          component: ScreenerPlanSelectionPage,
          onEnter: async (next, replace, callback) => {
            try {
              await getToolIfNotPresent("screener", (to) =>
                replace(to, { nextPathname: next.location.pathname }),
              );
              return requireScreener(next, replace);
            } catch {
              replace("/", { nextPathname: next.location.pathname });
            } finally {
              typeof callback === "function" && callback?.();
            }
          },
        },
        {
          path: "screener/:pageSlug",
          component: ScreenerPage,
          onEnter: async (next, replace, callback) => {
            try {
              await getToolIfNotPresent("screener", (to) =>
                replace(to, { nextPathname: next.location.pathname }),
              );
              return requireScreener(next, replace);
            } catch {
              replace("/", { nextPathname: next.location.pathname });
            } finally {
              typeof callback === "function" && callback?.();
            }
          },
        },
        {
          path: "progress/:toolSlug",
          component: ProgressPage,
          onEnter: async (next, replace, callback) => {
            try {
              await getToolIfNotPresent(next.params.toolSlug, (to) =>
                replace(to, { nextPathname: next.location.pathname }),
              );
              return requireTool()(next, replace);
            } catch {
              replace("/", { nextPathname: next.location.pathname });
            } finally {
              typeof callback === "function" && callback?.();
            }
          },
        },
        {
          path: ":toolSlug",
          component: ToolOverviewPage,
          onEnter: async (next, replace, callback) => {
            try {
              await getToolIfNotPresent(next.params.toolSlug, (to) =>
                replace(to, { nextPathname: next.location.pathname }),
              );
              await requireValidResponse("tool")(next, replace);
            } catch (e) {
              replace("/", { nextPathname: next.location.pathname });
            } finally {
              typeof callback === "function" && callback?.();
            }
          },
        },
        {
          path: ":toolSlug/:moduleSlug",
          component: ModuleOverviewPage,
          onEnter: async (next, replace, callback) => {
            try {
              await getToolIfNotPresent(next.params.toolSlug, (to) =>
                replace(to, { nextPathname: next.location.pathname }),
              );
              await requireValidResponse("module")(next, replace);
            } catch {
              replace("/", { nextPathname: next.location.pathname });
            } finally {
              typeof callback === "function" && callback?.();
            }
          },
        },
        {
          path: ":toolSlug/:moduleSlug/:sectionSlug",
          component: SectionOverviewPage,
          onEnter: async (next, replace, callback) => {
            try {
              const tool = await getToolIfNotPresent(
                next.params.toolSlug,
                (to) => replace(to, { nextPathname: next.location.pathname }),
              );
              await requireValidResponse("section")(next, replace);
            } catch (e) {
              replace("/", { nextPathname: next.location.pathname });
            } finally {
              typeof callback === "function" && callback?.();
            }
          },
        },
        {
          path: ":toolSlug/:moduleSlug/:sectionSlug/:pageSlug",
          component: DynamicPage,
          onEnter: async (next, replace, callback) => {
            try {
              await getToolIfNotPresent(next.params.toolSlug, (to) =>
                replace(to, { nextPathname: next.location.pathname }),
              );
              await requireValidResponse("page")(next, replace);
            } catch {
              replace("/", { nextPathname: next.location.pathname });
            } finally {
              typeof callback === "function" && callback?.();
            }
          },
        },
        {
          path: "*",
          component: LoginPage,
        },
      ],
    },
  ];
};
